import React, { ReactNode, useEffect, useRef } from "react";
import ReactDOM from "react-dom";

import { faUsers } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { bem } from "@react-md/utils";
import { Editor, Range } from "slate";
import { ReactEditor } from "slate-react";

import Team from "@mapmycustomers/shared/types/Team";
import User from "@mapmycustomers/shared/types/User";

import AvatarWithName from "../../../../../Avatar/AvatarWithName";
import { useConfigProvider } from "../../../../../ConfigProvider";
import MentionType from "../type/MentionType";
import { MENTION_LETTER_OFFSET } from "../util/const";

const block = bem("mmc-mentions-dropdown");

export const messages = {
  "ui.input.richtext.mentionsDropdown.integrationOnlyTag": "{crm} only",
  "ui.input.richtext.mentionsDropdown.noResult": "No results",
};

const Portal = ({ children }: { children: ReactNode }) => {
  return typeof document === "object" ? ReactDOM.createPortal(children, document.body) : null;
};

interface MentionsDropdownProps {
  activeIndex: number;
  editor: Editor;
  emptyTextForWithSkippedUsers?: string;
  onHover: (index: number, type: MentionType) => void;
  onSelect: (index: number, type: MentionType) => void;
  target: Range;
  teams: Team[];
  users: User[];
}

const MentionsDropdown = ({
  activeIndex,
  editor,
  emptyTextForWithSkippedUsers,
  onHover,
  onSelect,
  target,
  teams,
  users,
}: MentionsDropdownProps) => {
  const configProvider = useConfigProvider();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (target) {
      const el = ref.current;
      const domRange = ReactEditor.toDOMRange(editor, target);
      const rect = domRange.getBoundingClientRect();
      if (el) {
        let top = rect.top + window.pageYOffset + MENTION_LETTER_OFFSET;
        if (top + el.clientHeight > window.innerHeight) {
          top = rect.top + window.pageYOffset - el.clientHeight;
        }
        let left = rect.left + window.pageXOffset;
        if (left + el.clientWidth > window.innerWidth) {
          left = window.innerWidth - el.clientWidth;
        }
        el.style.top = `${top}px`;
        el.style.left = `${left}px`;
      }
    }
  }, [editor, target, users.length, teams.length]);

  return (
    <Portal>
      <div className={block("suggestions")} data-cy="mentions-portal" ref={ref}>
        {teams.map((team, i) => (
          <div
            className={block("suggestion", {
              highlighted: i === activeIndex,
            })}
            key={team.id}
            onClick={() => onSelect(i, MentionType.TEAM)}
            onMouseEnter={() => onHover(i, MentionType.TEAM)}
          >
            <div className={block("team")}>
              <div className={block("iconContainer")}>
                <FontAwesomeIcon className={block("teamIcon")} icon={faUsers} size="xs" />
              </div>
              <span>{team.name}</span>
            </div>
          </div>
        ))}
        {users.map((user, i) => {
          const integrationName = configProvider.getIntegrationNameIfIntegrationOnly?.(user);
          const tag = integrationName
            ? configProvider.formatMessage(
                "ui.input.richtext.mentionsDropdown.integrationOnlyTag",
                {
                  crm: integrationName,
                }
              )
            : undefined;
          return (
            <div
              className={block("suggestion", {
                highlighted: i + teams.length === activeIndex,
              })}
              key={user.id}
              onClick={() => onSelect(i + teams.length, MentionType.USER)}
              onMouseEnter={() => onHover(i + teams.length, MentionType.USER)}
            >
              <div className={block("user")}>
                <AvatarWithName className={block("userNameWrapper")} user={user} />
                {tag && <span className={block("userTag")}>{tag}</span>}
              </div>
            </div>
          );
        })}
        {users.length === 0 && (
          <div className={block("suggestion", { noResult: true })}>
            {emptyTextForWithSkippedUsers
              ? emptyTextForWithSkippedUsers
              : configProvider.formatMessage("ui.input.richtext.mentionsDropdown.noResult")}
          </div>
        )}
      </div>
    </Portal>
  );
};

export default MentionsDropdown;
